import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import QRCode from "qrcode";

const VERDICT_COPY = {
  green: {
    tag: "Compatible",
    line: "Vetted against Dr. Rey Hamidi's perimenopause skincare protocol — and it passed.",
  },
  yellow: {
    tag: "Save it for later",
    line: "Vetted against Dr. Rey Hamidi's perimenopause skincare protocol — not a fit for my current phase, but worth revisiting.",
  },
  red: {
    tag: "Skip this one",
    line: "Vetted against Dr. Rey Hamidi's perimenopause skincare protocol — and it works against the goals of my current phase.",
  },
};

const VERDICT_BG = {
  green: { card: "#e8efe7", ink: "#3f5343", accent: "#9bb09f" },
  yellow: { card: "#f3e9d2", ink: "#7a5b22", accent: "#c8a85e" },
  red: { card: "#f0d7d0", ink: "#7c3a2a", accent: "#c97a5e" },
};

/**
 * Single-image share card for a product verdict.
 * Renders an off-screen 1080x1080 (square) or 1080x1920 (story) DOM node and uses
 * html2canvas to export to PNG. Beige editorial aesthetic; signed Ageless Alchemy.
 *
 * Props:
 *   product: { product_name, category, verdict, summary, phase }
 *   onClose(): void
 */
export default function ShareCard({ product, onClose }) {
  const squareRef = useRef(null);
  const storyRef = useRef(null);
  const [busy, setBusy] = useState(null); // "square" | "story" | null
  const [qr, setQr] = useState("");
  const v = product.verdict || "green";
  const colors = VERDICT_BG[v] || VERDICT_BG.green;
  const copy = VERDICT_COPY[v] || VERDICT_COPY.green;

  // Generate the QR once on mount, pointing to the configured marketing URL
  // (falls back to the app's signup if env var isn't set).
  useEffect(() => {
    const url = process.env.REACT_APP_SHARE_URL || `${window.location.origin}/register?ref=share`;
    QRCode.toDataURL(url, {
      margin: 1,
      width: 320,
      color: { dark: "#2a2620", light: "#fbf6ec" },
    }).then(setQr).catch(() => setQr(""));
  }, []);

  const download = async (which) => {
    const node = which === "story" ? storyRef.current : squareRef.current;
    if (!node) return;
    setBusy(which);
    try {
      const canvas = await html2canvas(node, { scale: 1, backgroundColor: "#fbf6ec", useCORS: true });
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      const safe = (product.product_name || "verdict").replace(/[^a-z0-9]+/gi, "_").slice(0, 60);
      a.download = `ageless-alchemy_${safe}_${which}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setBusy(null);
    }
  };

  const CardBody = ({ format }) => (
    <div
      style={{
        width: format === "story" ? 1080 : 1080,
        height: format === "story" ? 1920 : 1080,
        background: "#fbf6ec",
        color: "#2a2620",
        fontFamily: "'Red Hat Display', sans-serif",
        padding: format === "story" ? "160px 110px" : "110px 110px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxSizing: "border-box",
      }}
    >
      {/* Top: eyebrow */}
      <div>
        <div style={{ fontSize: 28, letterSpacing: "0.18em", textTransform: "uppercase", color: "#7a6f60" }}>
          I vetted this with my Protocol Companion
        </div>
        <div style={{ marginTop: format === "story" ? 80 : 36, fontFamily: "'Playfair Display', serif", fontSize: format === "story" ? 96 : 76, lineHeight: 1.06, color: "#2a2620" }}>
          {product.product_name}
        </div>
        {product.category && (
          <div style={{ marginTop: 28, fontSize: 28, color: "#7a6f60", textTransform: "capitalize" }}>
            {String(product.category).replace(/_/g, " ")}
            {product.phase ? ` · ${String(product.phase).replace(/_/g, " ")}` : ""}
          </div>
        )}
      </div>

      {/* Verdict pill */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 24,
          padding: "26px 44px",
          borderRadius: 999,
          background: colors.card,
          color: colors.ink,
          alignSelf: "flex-start",
        }}
      >
        <span style={{ width: 32, height: 32, borderRadius: 999, background: colors.accent, display: "inline-block" }} />
        <span style={{ fontSize: 44, fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}>{copy.tag}</span>
      </div>

      {/* Summary line — leads with the app's value, not just the verdict */}
      <div style={{ fontSize: format === "story" ? 44 : 36, lineHeight: 1.32, color: "#3a342c", maxWidth: 920, fontFamily: "'Playfair Display', serif" }}>
        “{copy.line}”
      </div>

      {/* Value strip — what the app does */}
      <div style={{ display: "flex", alignItems: "center", gap: 18, fontSize: 24, color: "#5e544a", letterSpacing: "0.04em" }}>
        <span>Built for perimenopause skin</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>AI label analyzer</span>
        <span style={{ opacity: 0.5 }}>·</span>
        <span>Phase-aware verdicts</span>
      </div>

      {/* Footer mark with QR */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 32, borderTop: "1px solid #d9cfbe", paddingTop: 32 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 36 }}>
            Ageless <span style={{ fontStyle: "italic" }}>Alchemy</span>
          </div>
          <div style={{ fontSize: 22, color: "#7a6f60", letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Dr. Rey Hamidi · Ageless Alchemy Protocol Companion
          </div>
        </div>
        {qr && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <img src={qr} alt="" style={{ width: 140, height: 140, display: "block", borderRadius: 8 }} />
            <div style={{ fontSize: 18, color: "#7a6f60", letterSpacing: "0.14em", textTransform: "uppercase" }}>Scan to join</div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="share-card-overlay">
      <div className="bg-[var(--aa-bg)] w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-2xl border border-[var(--aa-elevated)] shadow-xl">
        <div className="sticky top-0 bg-[var(--aa-bg)] border-b border-[var(--aa-elevated)] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="aa-eyebrow">Share this verdict</div>
            <div className="font-display text-lg mt-1">Download a beige little card.</div>
          </div>
          <button onClick={onClose} data-testid="share-card-close" className="aa-pill px-3 py-1 hover:bg-[var(--aa-elevated)]">Close</button>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-[var(--aa-text-2)] mb-5 leading-relaxed">
            Pick a format — square for Instagram feeds and texts, vertical for Stories or TikTok.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="share-card-square"
              disabled={busy !== null}
              onClick={() => download("square")}
              className="aa-pill px-5 py-3 bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)] hover:opacity-90 disabled:opacity-60"
            >
              {busy === "square" ? "Rendering…" : "Download Square (1080×1080)"}
            </button>
            <button
              data-testid="share-card-story"
              disabled={busy !== null}
              onClick={() => download("story")}
              className="aa-pill px-5 py-3 hover:bg-[var(--aa-elevated)] disabled:opacity-60"
            >
              {busy === "story" ? "Rendering…" : "Download Story (1080×1920)"}
            </button>
          </div>

          {/* Live preview (scaled down) */}
          <div className="mt-6 border border-[var(--aa-elevated)] rounded-lg overflow-hidden" data-testid="share-card-preview">
            <div style={{ transform: "scale(0.32)", transformOrigin: "top left", width: 1080 * 0.32, height: 1080 * 0.32 }}>
              <CardBody format="square" />
            </div>
          </div>
        </div>
      </div>

      {/* Off-screen render targets */}
      <div style={{ position: "fixed", left: -99999, top: 0 }} aria-hidden="true">
        <div ref={squareRef}><CardBody format="square" /></div>
        <div ref={storyRef}><CardBody format="story" /></div>
      </div>
    </div>
  );
}
